
import MissionTabs from '@/components/Mission/MissionTabs.vue';
import Component from 'vue-class-component';
import MissionTabNames from '@/flightpath-components/services/missionTabNames';
import { Container } from 'inversify';
import { FlightPathServiceInterface } from '@/flightpath-components/services/FlightPathServiceInterface';
import SERVICE_IDENTIFIER from '@/flightpath-components/services/identifiers';
import { DeployedSinglePathMissionSummary } from '@/flightpath-components/models/DeployedSinglePathMissionSummary';
import { getModule } from 'vuex-module-decorators';
import FieldStore from '@/store/modules/fields';
import MissionStore from '@/store/modules/missions';
import MachineStore from '@/store/modules/machine';
import { Inject, Mixins, Watch } from 'vue-property-decorator';
import { DeployedSinglePathMissionQuery } from '@/flightpath-components/models/DeployedSinglePathMissionsQuery';
import { ImplementKeys, Parameters } from '@/services/Parameters';
import { MachineKey } from '@/flightpath-components/models/MachineKey';
import { SortHelper } from '@/services/SortHelper';
import PathStore from '@/store/modules/paths';
import { DeployedSinglePathMissionCloudState } from '@/flightpath-components/models/enums/DeployedSinglePathMissionCloudState';
import MissionToolTypes from '../../services/MissionToolTypes';
import LastModifiedMixIn from '@/mixins/lastModified';
import { NotificationMessageType } from '@/flightpath-components/models/NotificationMessageType';
import MissionReportStore from '@/store/modules/missionReport';
import GlobalStore from '@/store/modules/global';
import { FieldOperationsModel } from '@/flightpath-components/models/FieldOperationsModel';
import { SimplePath } from '@/flightpath-components/models/SimplePath';

// Define the component in class-style
/* eslint-disable @typescript-eslint/no-explicit-any */
@Component({ name: 'CompletedMissions', components: { MissionTabs } })
export default class CompletedMissions extends Mixins(LastModifiedMixIn) {
  public fieldStore = getModule(FieldStore, this.$store);
  public missionStore = getModule(MissionStore, this.$store);
  public missionReportStore = getModule(MissionReportStore, this.$store);
  public machineStore = getModule(MachineStore, this.$store);
  public pathStore = getModule(PathStore, this.$store);
  public globalStore = getModule(GlobalStore, this.$store);
  type = [NotificationMessageType.MissionCompleted, NotificationMessageType.MissionCancelled];

  missionTypes = MissionToolTypes.missionTypes;
  toolTypes = MissionToolTypes.toolTypes.filter((o) => o.value != 'weeder');
  tabName = MissionTabNames.CompletedMissions;
  loading = true;
  fieldsLoading = false;
  machinesLoading = false;
  pathsLoading = false;
  panels = [0, 1];

  showDatePicker = false;
  headers = [
    { text: this.$t('views.missions.name'), value: 'MissionName', align: 'left' },
    { text: this.$t('views.missions.status'), value: 'Status', align: 'left' },
    { text: this.$t('views.missions.machine'), value: 'TargetMachine.MachineName', align: 'left' },
    { text: this.$t('views.missions.field'), value: 'ReferenceFieldID', align: 'left' },
    { text: this.$t('views.missions.start_date'), value: 'UpdateTimeUTC', align: 'left' },
    { text: this.$t('views.missions.duration'), value: 'Duration', align: 'left' },
    { value: 'View', align: 'left' }
  ];
  windowSize = {
    x: 0,
    y: 0
  };
  millisecondsToHoursConversion = 36e5;

  get dates() {
    return this.missionReportStore.dates;
  }
  set dates(value: string[]) {
    this.missionReportStore.setDateRange(value);
  }

  get selectedMissionTypes() {
    return this.missionReportStore.selectedMissionTypes;
  }
  set selectedMissionTypes(value: string[]) {
    this.missionReportStore.setSelectedMissionTypes(value);
  }

  get selectedToolTypes() {
    return this.missionReportStore.selectedToolTypes;
  }
  set selectedToolTypes(value: string[]) {
    this.missionReportStore.setSelectedToolTypes(value);
  }

  get selectedFields() {
    return this.missionReportStore.selectedFields;
  }
  set selectedFields(value: string[]) {
    this.missionReportStore.setSelectedFields(value);
  }

  get selectedPaths() {
    return this.missionReportStore.selectedPaths;
  }
  set selectedPaths(value: string[]) {
    this.missionReportStore.setSelectedPaths(value);
  }

  get selectedMachines() {
    return this.missionReportStore.selectedMachines;
  }
  set selectedMachines(value: string[]) {
    this.missionReportStore.setSelectedMachines(value);
  }

  get sortBy() {
    return this.missionReportStore.sortIndex;
  }
  set sortBy(index: string) {
    this.missionReportStore.setTableSort(index);
  }

  get sortIsDesc() {
    return this.missionReportStore.sortIsDesc;
  }
  set sortIsDesc(isDesc: boolean[]) {
    this.missionReportStore.setTableIsDesc(isDesc);
  }

  get pageNumber() {
    return this.missionReportStore.tablePageNumber;
  }
  set pageNumber(page: number) {
    this.missionReportStore.setTablePageNumber(page);
  }

  onSortBy(sorts: string) {
    this.missionReportStore.setTableSort(sorts);
  }

  onIsDesc(isDesc: boolean[]) {
    this.missionReportStore.setTableIsDesc(isDesc);
  }

  onPageNumber(page: number) {
    this.missionReportStore.setTablePageNumber(page);
  }

  private flightPathService: FlightPathServiceInterface | undefined = undefined;
  @Inject('container')
  private container!: Container;

  created(): void {
    this.flightPathService = this.container.get<FlightPathServiceInterface>(SERVICE_IDENTIFIER.FLIGHT_PATH);
  }

  onResize() {
    this.windowSize = { x: window.innerWidth, y: window.innerHeight };
  }

  async mounted() {
    this.onResize();
    if (this.flightPathService != undefined) await this.flightPathService.updateToken();

    this.initDates();

    // initialize machines and fields if they haven't already been fetched from backend
    if (this.machineStore.machines == null || this.machineStore.machines.length == 0) {
      await this.getMachines();
    }
    if (this.fieldStore.availableFields == null || this.fieldStore.availableFields.length == 0) {
      await this.getFields();
    }
    if (this.pathStore.availablePaths == null || this.pathStore.availablePaths.length == 0) {
      await this.getpaths();
    }

    await this.queryMissions();
  }

  async getMachines(): Promise<void> {
    if (!this.flightPathService) return;

    this.machinesLoading = true;

    try {
      const machineResponse = await this.flightPathService.getAvailableMachines();
      if (machineResponse != null) {
        const json = JSON.stringify(machineResponse.data);
        const availableMachines = JSON.parse(json) as Array<MachineKey>;
        this.machineStore.setAvailableMachines(availableMachines);
      }
    } catch (error) {
      console.log(error);
    } finally {
      this.machinesLoading = false;
    }
  }

  async getFields(): Promise<void> {
    if (!this.flightPathService) return;

    this.fieldsLoading = true;

    try {
      const response = await this.flightPathService.getAvailableFields(this.globalStore.accountId);
      this.fieldStore.setFields(FieldOperationsModel.toModelCollection(JSON.stringify(response.data)));
    } catch (error) {
      console.log(error);
    } finally {
      this.fieldsLoading = false;
    }
  }

  async getpaths(): Promise<void> {
    if (!this.flightPathService) return;

    this.pathsLoading = true;
    try {
      const storedPaths = await this.flightPathService.getPathsByCustomerId(this.globalStore.accountId);
      this.pathStore.setAvailablePaths(SimplePath.toModelCollection(JSON.stringify(storedPaths.data)));
    } catch (error) {
      console.log(error);
    } finally {
      this.pathsLoading = false;
    }
  }

  async queryMissions(): Promise<void> {
    if (this.flightPathService == null || this.dates.length < 2) return;

    const queryDates = this.dates.map((x) => new Date(x).getTime());
    const startDate = new Date(Math.min.apply(null, queryDates));
    const endDate = new Date(Math.max.apply(null, queryDates));
    // round to next day, so we get all occurences on end date
    endDate.setUTCDate(endDate.getUTCDate() + 1);

    if (startDate == null || endDate == null) return;

    this.loading = true;

    const query = {
      StartDate: startDate,
      EndDate: endDate,
      CustomerID: this.globalStore.accountId
    } as DeployedSinglePathMissionQuery;

    try {
      const response = await this.flightPathService?.queryCompletedMissionsSummary(query);
      if (response?.status != 200) {
        console.log(response);
      } else {
        this.missionStore.setCompletedMissions(DeployedSinglePathMissionSummary.toModelCollection(JSON.stringify(response.data)));
      }
    } catch (error) {
      console.log(error);
    } finally {
      this.loading = false;
    }
  }

  @Watch('dates')
  async onDateRangeChanged(): Promise<void> {
    await this.queryMissions();
  }

  initDates(): void {
    if (this.missionReportStore.dates.length == 2 && this.dates.length != 0) {
      this.dates = this.missionReportStore.dates;
    } else {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 7);
      this.dates = [`${start.getFullYear()}-${start.getMonth() + 1}-${start.getDate()}`, `${end.getFullYear()}-${end.getMonth() + 1}-${end.getDate()}`];
    }
  }

  viewMissionReport(mission: DeployedSinglePathMissionSummary): void {
    this.$router.push({ name: 'MissionReport', params: { deploymentId: mission.MissionDeploymentID } });
  }

  getFieldNameFromReferenceID(id: string): string {
    const result = this.fieldStore.availableFields.find((x) => x.FieldID === id);

    return result != null ? result.FieldName : 'None';
  }

  getDuration(item: DeployedSinglePathMissionSummary): number {
    // divide by 36e5 to get hours from milliseconds
    const hours = (new Date(item.UpdateTimeUTC).getTime() - new Date(item.DeployedTimeUTC).getTime()) / this.millisecondsToHoursConversion;
    return Math.round((hours + Number.EPSILON) * 100) / 100;
  }

  humanReadableDuration(item: DeployedSinglePathMissionSummary): string {
    const msDuration = new Date(item.UpdateTimeUTC).getTime() - new Date(item.DeployedTimeUTC).getTime();
    const h = Math.floor(msDuration / 1000 / 60 / 60);
    const m = Math.floor((msDuration / 1000 / 60 / 60 - h) * 60);
    const s = Math.floor(((msDuration / 1000 / 60 / 60 - h) * 60 - m) * 60);

    // To get time format 00:00:00
    // const seconds: string = s < 10 ? `0${s}` : `${s}`;
    // const minutes: string = m < 10 ? `0${m}` : `${m}`;
    // const hours: string = h < 10 ? `0${h}` : `${h}`;

    return `${h} hrs ${m} min`;
  }

  getStartDateString(date: Date): string {
    date = new Date(date);
    return `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`;
  }

  getCloudStateString(mission: DeployedSinglePathMissionSummary): string {
    if (mission.MissionState == DeployedSinglePathMissionCloudState.Canceled) {
      return this.$t('views.missions.canceled') as string;
    } else if (mission.MissionState == DeployedSinglePathMissionCloudState.ConfirmCompleted) {
      return this.$t('views.missions.completed') as string;
    }
    return '';
  }

  sortTable(items: DeployedSinglePathMissionSummary[], index: string, isDesc: boolean[]): DeployedSinglePathMissionSummary[] {
    if (index == 'UpdateTimeUTC') {
      if (isDesc[0]) {
        items.sort((a, b) => SortHelper.compareDates(a.UpdateTimeUTC, b.UpdateTimeUTC));
      } else {
        items.sort((a, b) => SortHelper.compareDates(b.UpdateTimeUTC, a.UpdateTimeUTC));
      }
    }
    if (index == 'Duration') {
      if (isDesc[0]) {
        items.sort((a, b) => this.getDuration(a) - this.getDuration(b));
      } else {
        items.sort((a, b) => this.getDuration(b) - this.getDuration(a));
      }
    }
    if (index == 'TargetMachine.MachineName') {
      if (isDesc[0]) {
        items.sort((a, b) => SortHelper.compareStrings(a.TargetMachine.MachineName as string, b.TargetMachine.MachineName as string));
      } else {
        items.sort((a, b) => SortHelper.compareStrings(b.TargetMachine.MachineName as string, a.TargetMachine.MachineName as string));
      }
    }
    if (index == 'ReferenceFieldID') {
      if (isDesc[0]) {
        items.sort((a, b) =>
          SortHelper.compareStrings(this.getFieldNameFromReferenceID(a.ReferenceFieldID), this.getFieldNameFromReferenceID(b.ReferenceFieldID))
        );
      } else {
        items.sort((a, b) =>
          SortHelper.compareStrings(this.getFieldNameFromReferenceID(b.ReferenceFieldID), this.getFieldNameFromReferenceID(a.ReferenceFieldID))
        );
      }
    }
    if (index == 'MissionName') {
      if (isDesc[0]) {
        items.sort((a, b) => SortHelper.compareStrings(a.MissionName, b.MissionName));
      } else {
        items.sort((a, b) => SortHelper.compareStrings(b.MissionName, a.MissionName));
      }
    }
    if (index == 'Status') {
      if (isDesc[0]) {
        items.sort((a, b) => a.MissionState - b.MissionState);
      } else {
        items.sort((a, b) => b.MissionState - a.MissionState);
      }
    }
    return items;
  }

  get datesLabel(): string {
    const startDate = new Date(this.dates[0]);
    const endDate = new Date(this.dates[1]);

    const startDateString = `${startDate.getDate()}-${startDate.getMonth() + 1}-${startDate.getFullYear()}`;
    if (this.dates[1] != null) {
      return `${startDateString} ~ ${endDate.getDate()}-${endDate.getMonth() + 1}-${endDate.getFullYear()}`;
    } else {
      return `${startDateString} ~ `;
    }
  }

  get toolTypeFilterDisabled(): boolean {
    const disabled = this.missionReportStore.selectedMissionTypes.indexOf(ImplementKeys.tillage) == -1;
    if (disabled && this.missionReportStore.selectedToolTypes.length > 0) {
      this.selectedToolTypes = [];
    }
    return disabled;
  }

  get filteredImplementTypes(): string[] {
    let implementTypes: string[] = [];
    if (this.missionReportStore.selectedMissionTypes.indexOf(ImplementKeys.tillage) > -1) {
      if (this.missionReportStore.selectedToolTypes.length == 0) {
        implementTypes = this.toolTypes.map((x) => x.value.toString());
      } else {
        implementTypes = Object.assign([], this.selectedToolTypes) as string[];
      }
    }

    if (this.missionReportStore.selectedMissionTypes.indexOf(ImplementKeys.sprayer) > -1) {
      this.missionReportStore.pushSelectedMissionTypes(ImplementKeys.sprayer);
    }

    return implementTypes;
  }

  get filteredMissions(): DeployedSinglePathMissionSummary[] {
    let filteredMissions = this.missionStore.completedMissions;
    //filter mission type
    if (this.missionReportStore.selectedMissionTypes.length != 0 || this.selectedToolTypes.length != 0) {
      filteredMissions = filteredMissions.filter((x) => {
        const params = new Map(Object.entries(JSON.parse(x.ApplicationParameters)));
        const implementType = params.get(Parameters.implementType) as string;
        return this.filteredImplementTypes.indexOf(implementType) > -1;
      });
    }

    // filter fields
    if (this.missionReportStore.selectedFields.length != 0) {
      filteredMissions = filteredMissions.filter((x) => {
        return this.selectedFields.indexOf(x.ReferenceFieldID) > -1;
      });
    }

    // filter machines
    if (this.missionReportStore.selectedMachines.length != 0) {
      filteredMissions = filteredMissions.filter((x) => {
        return this.selectedMachines.indexOf(x.TargetMachine.machineId) > -1;
      });
    }

    // filter paths
    if (this.missionReportStore.selectedPaths.length != 0) {
      filteredMissions = filteredMissions.filter((x) => {
        return this.selectedPaths.indexOf(x.ReferencePathID) > -1;
      });
    }

    return filteredMissions;
  }
}
